import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, ArrowRight, X } from '@phosphor-icons/react';

// Tour slide images - stored in public/tour/
const SLIDE_IMAGES = {
  'dashboard': '/tour/slide1-dashboard.png',
  'requisitions': '/tour/slide2-requisitions.png',
  'purchase-orders': '/tour/slide3-purchase-orders.png',
  'vendor-portal': '/tour/slide4-vendor-portal.png',
  'freight-portal': '/tour/slide5-freight-portal.png',
  'fc-approval': '/tour/slide6-fc-approval.png',
  'reports': '/tour/slide7-reports.png',
  'po-journey': '/tour/slide8-po-journey.png'
};

const TOUR_SLIDES = [
  {
    id: 1,
    headline: 'Your Entire Procurement Operation In One View',
    description: 'Everything you need to know about your procurement operation the moment you log in. Overdue alerts, active orders, and real time status — all in one place.',
    imageAlt: 'Dashboard showing overdue alert banner, summary cards, and Order Status Pipeline',
    imagePlaceholder: 'dashboard'
  },
  {
    id: 2,
    headline: 'From Staff Request To Purchase Order In Minutes',
    description: 'A structured request process that gives every department a voice while keeping the Purchasing Manager in complete control. No more verbal requests. No more lost emails.',
    imageAlt: 'Purchase Requisitions page with pending conversion tab',
    imagePlaceholder: 'requisitions'
  },
  {
    id: 3,
    headline: 'Professional Purchase Orders Built For Caribbean Hospitality',
    description: 'Create and manage purchase orders with full visibility of where every order is and who has it at every moment. No more chasing vendors by phone or WhatsApp.',
    imageAlt: 'Purchase Orders list with Currently With labels and status badges',
    imagePlaceholder: 'purchase-orders'
  },
  {
    id: 4,
    headline: 'Your Vendors Have Their Own Secure Portal',
    description: 'Every vendor works within their own dedicated space. Documents are uploaded, confirmed, and tracked through the platform — not through scattered email chains.',
    imageAlt: 'Vendor Portal PO detail with Confirm Receipt button and document upload',
    imagePlaceholder: 'vendor-portal'
  },
  {
    id: 5,
    headline: 'Shipment Tracking From Port To Property',
    description: 'Your freight forwarders log in, access everything they need, and update shipment status in real time. You see every movement without making a single phone call.',
    imageAlt: 'Freight Forwarder Portal with shipment details and vendor documents',
    imagePlaceholder: 'freight-portal'
  },
  {
    id: 6,
    headline: 'Financial Controls That Work The Way You Do',
    description: 'Your Financial Controller has a dedicated space built specifically for their role. Reviews, approvals, and sign offs — all handled digitally within the platform.',
    imageAlt: 'Financial Controller Approval Queue with Approve, Reject, Query buttons',
    imagePlaceholder: 'fc-approval'
  },
  {
    id: 7,
    headline: 'The Data Your Leadership Team Has Always Needed',
    description: 'Instant access to the procurement reports that used to take hours to compile. The information is always there — current, accurate, and exportable.',
    imageAlt: 'Reports page with Spend by Department chart and table',
    imagePlaceholder: 'reports'
  },
  {
    id: 8,
    headline: 'A Complete Audit Trail From Requisition To Delivery',
    description: 'Every purchase order tells its complete story. Every person. Every action. Every timestamp. From the moment a department head submits a request to the moment goods are on their way — nothing is ever lost or forgotten.',
    imageAlt: 'PO Journey Pipeline with eight stages and timestamps',
    imagePlaceholder: 'po-journey'
  }
];

const SLIDE_TITLES = [
  'The Dashboard',
  'Purchase Requisitions',
  'Purchase Orders',
  'Vendor Portal',
  'Freight Forwarder Portal',
  'Financial Controller Portal',
  'Reports',
  'PO Journey Pipeline'
];

const ProductTourPage = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showEndScreen, setShowEndScreen] = useState(false);

  const totalSlides = TOUR_SLIDES.length;
  const slide = TOUR_SLIDES[currentSlide];

  const goNext = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      setShowEndScreen(true);
    }
  };

  const goBack = () => {
    if (showEndScreen) {
      setShowEndScreen(false);
    } else if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const closeTour = () => {
    navigate('/');
  };

  // End screen after all slides
  if (showEndScreen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#3D1D72] to-[#5B2D9E] flex flex-col items-center justify-center px-4">
        <div className="text-center max-w-2xl">
          {/* Logo */}
          <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-8 shadow-xl">
            <span className="text-white font-bold text-4xl" style={{ fontFamily: "'Outfit', sans-serif" }}>H</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            HERChain Solutions
          </h1>
          
          <p className="text-xl text-white/80 mb-12" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Built For Caribbean Hospitality
          </p>
          
          <Button
            size="lg"
            className="bg-[#C9A84C] hover:bg-[#B8973D] text-white px-12 py-6 text-lg font-semibold shadow-xl"
            onClick={() => navigate('/?demo=true')}
            data-testid="tour-request-demo-btn"
          >
            Request a Demo
          </Button>
          
          <p className="text-white/60 text-sm mt-8">
            Already have access? Contact{' '}
            <a href="mailto:support@herchainsolutions.com" className="text-white/80 hover:text-white underline">
              support@herchainsolutions.com
            </a>
          </p>
        </div>
        
        {/* Back button */}
        <button
          onClick={goBack}
          className="absolute bottom-8 left-8 flex items-center gap-2 text-white/70 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Tour</span>
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#3D1D72] to-[#5B2D9E] flex items-center justify-center">
              <span className="text-white font-bold text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>H</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#3D1D72]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Product Tour
              </h1>
            </div>
          </div>
          
          {/* Progress indicator */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-slate-600">
              Slide {currentSlide + 1} of {totalSlides}
            </span>
            <div className="flex gap-1.5">
              {TOUR_SLIDES.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    idx === currentSlide ? 'bg-[#3D1D72]' : idx < currentSlide ? 'bg-[#3D1D72]/40' : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>
          </div>
          
          {/* Skip Tour & Close */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowEndScreen(true)}
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
              data-testid="skip-tour-btn"
            >
              Skip Tour
            </button>
            <button
              onClick={closeTour}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Close tour"
            >
              <X size={24} className="text-slate-500" />
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col lg:flex-row">
        {/* Left side - Text content */}
        <div className="lg:w-2/5 p-8 lg:p-12 flex flex-col justify-center bg-white">
          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#3D1D72]/10 rounded-full text-[#3D1D72] text-sm font-medium mb-6">
              {SLIDE_TITLES[currentSlide]}
            </div>
            
            <h2 className="text-3xl lg:text-4xl font-bold text-[#3D1D72] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {slide.headline}
            </h2>
            
            <p className="text-lg text-slate-600 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              {slide.description}
            </p>
          </div>
        </div>

        {/* Right side - Screenshot */}
        <div className="lg:w-3/5 p-6 lg:p-8 flex items-center justify-center bg-[#F8FAFC]">
          <div className="w-full max-w-4xl aspect-[16/10] bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            {/* Real platform screenshot */}
            <img 
              src={SLIDE_IMAGES[slide.imagePlaceholder]} 
              alt={slide.imageAlt}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </main>

      {/* Footer navigation */}
      <footer className="bg-white border-t border-slate-200 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button
            variant="outline"
            onClick={goBack}
            disabled={currentSlide === 0}
            className="border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            data-testid="tour-back-btn"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back
          </Button>
          
          <Button
            onClick={goNext}
            className="bg-[#3D1D72] hover:bg-[#2D1556] text-white"
            data-testid="tour-next-btn"
          >
            {currentSlide < totalSlides - 1 ? (
              <>
                Next
                <ArrowRight size={18} className="ml-2" />
              </>
            ) : (
              <>
                See Demo Options
                <ArrowRight size={18} className="ml-2" />
              </>
            )}
          </Button>
        </div>
      </footer>
    </div>
  );
};

export default ProductTourPage;
